/**
 * 管理员后台 - 状态管理
 * 独立于普通用户的管理员认证状态
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import * as adminApi from '@/api/admin';

// 管理员角色类型
export type AdminRole = 'super_admin' | 'content_reviewer' | 'operator';

// 管理员信息类型
export interface AdminInfo {
  id: string;
  username: string;
  nickname: string | null;
  avatar: string | null;
  role: AdminRole;
  lastLoginAt: string | null;
  createdAt: string;
}

// 登录参数类型
export interface AdminLoginParams {
  username: string;
  password: string;
}

/**
 * 管理员状态管理 Store
 */
export const useAdminStore = defineStore('admin', () => {
  // ============================================
  // 状态定义
  // ============================================

  /** 当前管理员信息 */
  const admin = ref<AdminInfo | null>(null);

  /** 管理员访问令牌 */
  const token = ref<string | null>(localStorage.getItem('adminToken'));

  /** 登录加载状态 */
  const loading = ref(false);

  // ============================================
  // 计算属性
  // ============================================

  /** 是否已登录 */
  const isLoggedIn = computed(() => !!token.value);

  /** 管理员角色 */
  const role = computed(() => admin.value?.role ?? null);

  /** 是否是超级管理员 */
  const isSuperAdmin = computed(() => admin.value?.role === 'super_admin');

  /** 显示名称（优先昵称，其次用户名） */
  const displayName = computed(() => admin.value?.nickname || admin.value?.username || '');

  // ============================================
  // 权限检查方法
  // ============================================

  /**
   * 检查是否有指定模块的权限
   */
  function hasPermission(module: string): boolean {
    if (!admin.value) return false;
    
    const permissions: Record<AdminRole, string[]> = {
      super_admin: [
        'admin_manage', 'work_review', 'comment_manage', 'asset_manage',
        'template_manage', 'tag_manage', 'config_manage', 'sensitive_word',
        'announcement', 'stats_view', 'task_monitor', 'operation_log'
      ],
      content_reviewer: ['work_review', 'comment_manage', 'sensitive_word', 'stats_view'],
      operator: ['asset_manage', 'template_manage', 'tag_manage', 'announcement', 'stats_view', 'task_monitor']
    };

    return permissions[admin.value.role]?.includes(module) ?? false;
  }

  // ============================================
  // 方法定义
  // ============================================

  /**
   * 设置管理员信息
   */
  function setAdmin(adminData: AdminInfo) {
    admin.value = adminData;
  }

  /**
   * 设置令牌
   */
  function setToken(newToken: string) {
    console.log('🔑 setToken 被调用，token长度:', newToken.length);
    token.value = newToken;
    localStorage.setItem('adminToken', newToken);
    
    // 验证保存
    const saved = localStorage.getItem('adminToken');
    console.log('✓ Token已保存到localStorage:', saved ? '成功' : '失败');
  }

  /**
   * 清除管理员状态（登出）
   */
  function clearAdmin() {
    admin.value = null;
    token.value = null;
    localStorage.removeItem('adminToken');
  }

  /**
   * 管理员登录
   */
  async function login(params: AdminLoginParams) {
    loading.value = true;
    try {
      const response = await adminApi.login(params);
      setAdmin(response.admin);
      setToken(response.token);
      return response;
    } finally {
      loading.value = false;
    }
  }

  /**
   * 获取管理员信息
   */
  async function fetchAdminProfile() {
    if (!token.value) return null;
    
    try {
      const adminInfo = await adminApi.getProfile();
      setAdmin(adminInfo);
      return adminInfo;
    } catch (error) {
      // Token无效，清除状态
      clearAdmin();
      throw error;
    }
  }

  /**
   * 修改密码
   */
  async function changePassword(oldPassword: string, newPassword: string) {
    return adminApi.changePassword(oldPassword, newPassword);
  }

  /**
   * 登出
   */
  function logout() {
    clearAdmin();
  }

  /**
   * 初始化管理员状态（应用启动时调用）
   */
  async function initAdminState() {
    if (token.value) {
      try {
        await fetchAdminProfile();
      } catch {
        // 获取信息失败，清除状态
        clearAdmin();
      }
    }
  }

  return {
    // 状态
    admin,
    token,
    loading,
    // 计算属性
    isLoggedIn,
    role,
    isSuperAdmin,
    displayName,
    // 方法
    hasPermission,
    setAdmin,
    setToken,
    clearAdmin,
    login,
    fetchAdminProfile,
    changePassword,
    logout,
    initAdminState
  };
});
